import { useState } from "react";
import { getEncryptedBody, getDecryptedData } from "../../../utils/common";
import { toast } from "react-toastify";

interface UseScheduleVisitV2Props {
  location: any;
  patientData: any;
  userDetails: any;
  editedDetails: any;
  navigate: any;
}

const useScheduleVisitV2 = ({
  location,
  patientData,
  userDetails,
  editedDetails,
  navigate,
}: UseScheduleVisitV2Props) => {
  const [schedulingVisit, setSchedulingVisit] = useState(false);

  const scheduleVisitDetailsV2 = async () => {
    setSchedulingVisit(true);
    try {
      const token = localStorage.getItem("token");
      const secretKey = process.env.REACT_APP_HEALTH_SECRET_KEY || '';

      // Build primary_drugs array with is_free inside - use selected primary drug or fall back to location state
      const selectedPrimaryDrug = editedDetails?.primary_drug || location.state?.support_program_drug;
      const primaryDrugs = [
        {
          drug_name: selectedPrimaryDrug,
          is_free: String(
            editedDetails?.is_primary_drug_free !== undefined
              ? editedDetails?.is_primary_drug_free
              : 0
          ), // Ensure string: "0" or "1"
        },
      ];

      // Build secondary_drugs array if any are selected
      // Each drug object should have drug_name and is_free
      // Filter out empty rows and map to clean objects
      const secondaryDrugs =
        editedDetails?.secondary_drugs && editedDetails?.secondary_drugs.length > 0
          ? editedDetails.secondary_drugs
              .filter((drug: any) => drug.drug_name && drug.drug_name !== "")
              .map((drug: any) => ({
                drug_name: drug.drug_name,
                is_free: String(drug.is_free !== undefined ? drug.is_free : 0), // Ensure string
              }))
          : [];
      
      const hasSecondaryDrugs = secondaryDrugs.length > 0;

      // Build visit_meta_info object
      const visitMetaInfo: any = {
        target_therapy_drug: selectedPrimaryDrug, // Use selected primary drug
        primary_drugs: primaryDrugs, // is_free is inside each drug object
        therapy_type: editedDetails?.therapy_type || location.state?.visitMetaInfo?.therapy_type,
        cancer_type: editedDetails?.cancer_type || location.state?.visitMetaInfo?.cancer_type,
        notes: location.state?.visitMetaInfo?.notes,
        visit_cycle: 1 + Number(location.state?.cycleNumber), // Incremented from the current visit
        // Note: is_primary_drug_free removed - is_free is now inside primary_drugs array
      };

      // Only include therapy_indication if it has a value (not empty string)
      // This means the therapy has indications and user selected one
      // IMPORTANT: Only use editedDetails, NOT location.state (to avoid carrying over old values)
      const indicationValue = editedDetails?.therapy_indication;
      
      if (indicationValue && indicationValue !== "") {
        visitMetaInfo.therapy_indication = indicationValue;
      }

      // Add dosage if present (for Nivolumab)
      if (editedDetails?.upcoming_dosage) {
        visitMetaInfo.dosage = editedDetails?.upcoming_dosage;
      }

      // Add brain_mets if present (for Tucatinib)
      if (location.state?.visitMetaInfo?.brain_mets !== undefined) {
        visitMetaInfo.brain_mets = location.state?.visitMetaInfo?.brain_mets;
      }

      // Add secondary drugs information if any are selected
      // Each drug object already contains drug_name and is_free
      if (hasSecondaryDrugs) {
        visitMetaInfo.secondary_drugs = secondaryDrugs;
        // Note: is_secondary_drug_free removed - is_free is now inside each drug object
      }

      // Build the main payload
      const body: any = {
        patient_id: location.state?.patientId,
        patient_phone: patientData.contact_no,
        patient_name: patientData.name,
        follow_up_date: editedDetails?.next_cycle_date,
        updated_by: userDetails.health_coach_id,
        support_program_drug: selectedPrimaryDrug, // Use selected primary drug
        visit_meta_info: visitMetaInfo,
      };

      // Add care coins if specified
      if (editedDetails?.care_coins && Number(editedDetails?.care_coins) > 0) {
        body.assign_reward = 1;
        body.assign_reward_params = {
          amount: parseInt(editedDetails?.care_coins) * 100, // Convert to smallest currency unit
          transactionType: "CYCLE_COMPLETION",
          notes: {
            type: "upcoming_cycle_scheduled",
          },
          patientId: location.state?.patientId,
        };
      } else {
        body.assign_reward = 0;
      }

      const enCryptedBody = getEncryptedBody(body);

      const ScheduleVisitAPI = `${process.env.REACT_APP_API_URL}/api/v8/healthcoach/schedule_patient_visit`;
//return
      const response = await fetch(ScheduleVisitAPI, {
        method: "POST",
        headers: {
          "Content-Type": "text/plain",
          token: `${token}`,
          health_secret: secretKey,
        },
        body: `${enCryptedBody}`,
      });

      if (!response.ok) {
        throw new Error("Failed to schedule a patient visit");
      }

      const data = await response.json();
      const decryptedData = getDecryptedData(data);

      toast.success("Upcoming cycle scheduled successfully!");

      // Navigate to patient summary
      const navigationToken = localStorage.getItem("token");
      navigate(`/patient-summary/:token`, {
        state: { patientId: location.state?.patientId, token: navigationToken },
      });

      setSchedulingVisit(false);
    } catch (error) {
      console.error("Error scheduling visit:", error);
      toast.error("Failed to schedule upcoming cycle. Please try again.");
      setSchedulingVisit(false);
    }
  };

  return {
    schedulingVisit,
    scheduleVisitDetailsV2,
  };
};

export default useScheduleVisitV2;
